import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Separator } from './ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Facebook, Twitter, Instagram, Linkedin, Mail, Phone, MapPin, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '@/pages/providers/AppProvider';
import { normalizeMediaUrl } from '@/utils/media';
import { APP_CONFIG } from '@/config';

const defaultLogo = '/images/Megajoblogo-removebg-preview.png';

export function FooterEnhanced() {
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState({ title: '', content: '' });
  const { siteSettings } = useApp();
  const companyLogo = normalizeMediaUrl(siteSettings?.logoUrl) || defaultLogo;

  const locationText = String(siteSettings?.address || siteSettings?.companyLocation || APP_CONFIG.company.location);
  const primaryPhone = String(siteSettings?.phone || siteSettings?.mainPhone || APP_CONFIG.company.phone);
  const primaryEmail = String(siteSettings?.email || siteSettings?.supportEmail || APP_CONFIG.emails.contact);
  const facebookUrl = String(siteSettings?.facebook || APP_CONFIG.socials.facebook);
  const twitterUrl = String(siteSettings?.twitter || APP_CONFIG.socials.twitter);
  const instagramUrl = String(siteSettings?.instagram || APP_CONFIG.socials.instagram);
  const linkedinUrl = String(siteSettings?.linkedin || APP_CONFIG.socials.linkedin);
  const companyName = String(siteSettings?.companyName || APP_CONFIG.siteName);
  const establishedYear = siteSettings?.establishedYear || String(new Date().getFullYear());

  // Navigation helper function
  const navigateTo = (path: string) => {
    window.open(path, '_self');
  };

  // Open external link in new tab
  const openExternal = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Show modal with content
  const showModalContent = (title: string, content: string) => {
    setModalContent({ title, content });
    setShowModal(true);
  };

  // Newsletter subscription
  const handleNewsletterSubmit = () => {
    if (!newsletterEmail) {
      toast.error('Please enter your email address');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newsletterEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Simulate newsletter subscription
    toast.success('Successfully subscribed to job alerts!');
    setNewsletterEmail('');
  };

  const quickLinks = [
    { name: 'About Us', action: () => navigateTo('/about') },
    { name: 'How It Works', action: () => showModalContent('How It Works', 'MegaJobNepal connects job seekers with employers through our comprehensive platform. Job seekers can browse jobs, apply online, and track applications. Employers can post jobs, search resumes, and manage candidates efficiently.') },
    { name: 'Pricing', action: () => navigateTo('/employers') },
    { name: 'FAQs', action: () => showModalContent('Frequently Asked Questions', 'Q: How do I create an account?\nA: Click the Login/Register button and fill out the form.\n\nQ: Is the service free for job seekers?\nA: Yes, all job seeker features are completely free.\n\nQ: How do I post a job?\nA: Employers can post jobs through the employer dashboard after registration.\n\nQ: How do I edit my profile?\nA: Log in to your dashboard to edit your profile information.') },
    { name: 'Contact Us', action: () => navigateTo('/contact') },
    { name: 'Blog', action: () => navigateTo('/blogs') }
  ];

  const forJobSeekers = [
    { name: 'Browse Jobs', action: () => navigateTo('/jobs') },
    { name: 'Career Advice', action: () => navigateTo('/blogs') },
    { name: 'Resume Builder', action: () => showModalContent('Resume Builder', 'Create a professional resume using our built-in resume builder. Log in to your job seeker dashboard to access this feature and create compelling resumes that stand out to employers.') },
    { name: 'Interview Tips', action: () => navigateTo('/blogs') },
    { name: 'Salary Guide', action: () => showModalContent('Salary Guide', 'Get insights into salary ranges for different positions across Nepal. Our salary guide helps you understand market rates and negotiate better compensation packages.') },
    { name: 'Job Alerts', action: () => showModalContent('Job Alerts', 'Set up personalized job alerts based on your preferences. Get notified when new jobs matching your criteria are posted. Available in your job seeker dashboard.') }
  ];

  const forEmployers = [
    { name: 'Post a Job', action: () => navigateTo('/employer/dashboard') },
    { name: 'Browse Resumes', action: () => navigateTo('/employer/dashboard') },
    { name: 'Recruiting Solutions', action: () => navigateTo('/employers') },
    { name: 'Employer Branding', action: () => navigateTo('/employers') },
    { name: 'Hiring Events', action: () => showModalContent('Hiring Events', 'Organize and participate in virtual hiring events to connect with top talent. Contact our team to learn more about hosting hiring events on our platform.') },
    { name: 'Talent Solutions', action: () => navigateTo('/employers') }
  ];

  const categories = [
    { name: 'Information Technology', action: () => navigateTo('/jobs?category=Information Technology') },
    { name: 'Banking & Finance', action: () => navigateTo('/jobs?category=Banking & Finance') },
    { name: 'Sales & Marketing', action: () => navigateTo('/jobs?category=Sales & Marketing') },
    { name: 'Engineering', action: () => navigateTo('/jobs?category=Engineering') },
    { name: 'Healthcare', action: () => navigateTo('/jobs?category=Healthcare') },
    { name: 'Education', action: () => navigateTo('/jobs?category=Education') }
  ];

  return (
    <footer className="bg-gray-900 text-white">
      {/* Newsletter Section */}
      <div className="bg-blue-600 py-8 sm:py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h3 className="text-2xl font-bold mb-3">Stay Updated with Latest Jobs</h3>
            <p className="text-blue-100 mb-4">Subscribe to our newsletter and get job alerts directly in your inbox</p>
            <div className="max-w-md mx-auto flex flex-col sm:flex-row gap-3 sm:gap-2">
              <Input
                type="email"
                placeholder="Enter your email address"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleNewsletterSubmit()}
                className="bg-white text-gray-900 border-0 h-11"
              />
              <Button
                onClick={handleNewsletterSubmit}
                className="bg-gray-900 hover:bg-gray-800 text-white h-11 px-8"
              >
                Subscribe
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="py-8 sm:py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-5 gap-y-10 gap-x-8">

            {/* Company Info */}
            <div className="col-span-2 lg:col-span-1">
              <div className="flex items-center mb-4">
                <img
                  src={companyLogo}
                  alt="MegaJob"
                  className="h-10 w-auto object-contain"
                />
              </div>
              <p className="text-gray-400 mb-2 leading-tight">
                Nepal's leading job portal connecting talented professionals with top employers across the country.
              </p>
              <div className="space-y-1 text-sm text-gray-400">
                <div className="flex items-center space-x-2">
                  <MapPin className="w-4 h-4" />
                  <span>{locationText}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Phone className="w-4 h-4" />
                  <a href={`tel:${primaryPhone.replace(/\s+/g, '')}`} className="hover:text-white">{primaryPhone}</a>
                </div>
                <div className="flex items-center space-x-2">
                  <Mail className="w-4 h-4" />
                  <a href={`mailto:${primaryEmail}`} className="hover:text-white">{primaryEmail}</a>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-semibold mb-2">Quick Links</h4>
              <ul className="space-y-1">
                {quickLinks.map((link, index) => (
                  <li key={index}>
                    <Button
                      variant="link"
                      className="text-gray-400 hover:text-white p-0 h-auto justify-start leading-tight"
                      onClick={link.action}
                    >
                      {link.name}
                    </Button>
                  </li>
                ))}
              </ul>
            </div>

            {/* For Job Seekers */}
            <div>
              <h4 className="text-lg font-semibold mb-2">For Job Seekers</h4>
              <ul className="space-y-1">
                {forJobSeekers.map((link, index) => (
                  <li key={index}>
                    <Button
                      variant="link"
                      className="text-gray-400 hover:text-white p-0 h-auto justify-start leading-tight"
                      onClick={link.action}
                    >
                      {link.name}
                    </Button>
                  </li>
                ))}
              </ul>
            </div>

            {/* For Employers */}
            <div>
              <h4 className="text-lg font-semibold mb-2">For Employers</h4>
              <ul className="space-y-1">
                {forEmployers.map((link, index) => (
                  <li key={index}>
                    <Button
                      variant="link"
                      className="text-gray-400 hover:text-white p-0 h-auto justify-start leading-tight"
                      onClick={link.action}
                    >
                      {link.name}
                    </Button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Job Categories */}
            <div>
              <h4 className="text-lg font-semibold mb-2">Popular Categories</h4>
              <ul className="space-y-1">
                {categories.map((category, index) => (
                  <li key={index}>
                    <Button
                      variant="link"
                      className="text-gray-400 hover:text-white p-0 h-auto justify-start leading-tight"
                      onClick={category.action}
                    >
                      {category.name}
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      <Separator className="bg-gray-800" />



      {/* Bottom Footer */}
      <div className="py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-1 md:space-y-0">

            {/* Copyright */}
            <div className="text-gray-400 text-sm">
              <p>&copy; {establishedYear} {companyName.endsWith('.') ? companyName.slice(0, -1) : companyName}. All rights reserved.</p>
            </div>

            {/* Legal Links */}
            <div className="flex flex-col items-center">
              <div className="flex space-x-3 text-sm">
                <Button
                  variant="link"
                  className="text-gray-400 hover:text-white p-0 h-auto"
                  onClick={() => navigateTo('/privacy')}
                >
                  Privacy Policy
                </Button>
                <Button
                  variant="link"
                  className="text-gray-400 hover:text-white p-0 h-auto"
                  onClick={() => showModalContent('Terms of Service', 'By using MegaJobNepal, you agree to our terms and conditions. These terms govern your use of our platform and services.')}
                >
                  Terms of Service
                </Button>
                <Button
                  variant="link"
                  className="text-gray-400 hover:text-white p-0 h-auto"
                  onClick={() => showModalContent('Cookie Policy', 'We use cookies to enhance your browsing experience and provide personalized content. Learn more about our cookie usage and how to manage your preferences.')}
                >
                  Cookie Policy
                </Button>
              </div>
              <p className="text-gray-500 font-bold text-sm mt-1">
                {atob('RGV2ZWxvcGVkIGJ5')}{' '}
                <a
                  href={atob('aHR0cHM6Ly93d3cudGhha3VyZGlwYWsuY29tLm5wLw==')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors underline"
                >
                  {atob('RGlwYWsgVGhha3Vy')}
                </a>
              </p>
            </div>

            {/* Social Media */}
            <div className="flex space-x-4">
              <Button
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-white hover:bg-gray-800"
                onClick={() => openExternal(facebookUrl)}
              >
                <Facebook className="w-5 h-5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-white hover:bg-gray-800"
                onClick={() => openExternal(twitterUrl)}
              >
                <Twitter className="w-5 h-5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-white hover:bg-gray-800"
                onClick={() => openExternal(instagramUrl)}
              >
                <Instagram className="w-5 h-5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-white hover:bg-gray-800"
                onClick={() => openExternal(linkedinUrl)}
              >
                <Linkedin className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile App Promotion */}
      <div className="bg-gray-800 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h4 className="text-lg font-semibold mb-2">Get the MegaJob App</h4>
            <p className="text-gray-400 mb-3">Apply for jobs on the go. Download our mobile app.</p>
            <div className="flex justify-center space-x-3">
              <Button
                variant="outline"
                className="bg-transparent border-gray-600 text-white hover:bg-gray-700"
                onClick={() => openExternal('https://play.google.com/store')}
              >
                <div className="flex items-center space-x-2">
                  <div className="w-6 h-6 bg-white rounded-sm flex items-center justify-center">
                    <span className="text-xs text-gray-900 font-bold">▶</span>
                  </div>
                  <div className="text-left">
                    <div className="text-xs">Get it on</div>
                    <div className="font-semibold">Google Play</div>
                  </div>
                </div>
              </Button>
              <Button
                variant="outline"
                className="bg-transparent border-gray-600 text-white hover:bg-gray-700"
                onClick={() => openExternal('https://apps.apple.com')}
              >
                <div className="flex items-center space-x-2">
                  <div className="w-6 h-6 bg-white rounded-sm flex items-center justify-center">
                    <span className="text-xs text-gray-900 font-bold">🍎</span>
                  </div>
                  <div className="text-left">
                    <div className="text-xs">Download on the</div>
                    <div className="font-semibold">App Store</div>
                  </div>
                </div>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal for displaying content */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl" aria-describedby="modal-description">
          <DialogHeader>
            <DialogTitle>{modalContent.title}</DialogTitle>
            <DialogDescription id="modal-description" className="whitespace-pre-line text-left">
              {modalContent.content}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end">
            <Button onClick={() => setShowModal(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </footer >
  );
}

